import { useState, MouseEventHandler } from 'react';

import { useQuery } from '@tanstack/react-query';

import { LocationIcon } from '@assets/icons';
import SearchBarSearchInput from '@components/ui/search/SearchBarInputNext/SearchBarSearchInput/SearchBarSearchInput';
import CustomIcon from '@components/utils/CustomIcon/index';
import { useDebounceValue } from '@hooks/hooks';
import { SEARCH_LENGTH } from '@utils/utils';
import { SearchViewModel, WebUrlType } from '@api/v3';

import { HotelSeachWrapper } from './styled';
import { SearchResult } from './SearchResult';
import {
  BookerSearchViewModel,
  BookerWebUrlType,
} from './BookerSearchViewModel';

export const groupItemsByTypes = (
  items: BookerSearchViewModel[],
  types: (WebUrlType | BookerWebUrlType)[]
) => {
  const groupedItems: BookerSearchViewModel[] = [];

  items.forEach((item) => {
    if (item?.type && types.includes(item?.type)) {
      groupedItems.push(item);
    }
  });

  return groupedItems;
};

interface BookerSearchDrawerProps {
  type: string;
  placeholder: string;
  onInputLocationChange?: (item: BookerSearchViewModel) => void;
  responsiveTitle: string;
  fetchItems: (param: string) => Promise<BookerSearchViewModel[]>;
  onHrefUpdate?: (href: string) => void;
  onSearchItemTextUpdate?: (itemText: string) => void;
  history?: BookerSearchViewModel[];
  onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  callBack?: (item: BookerSearchViewModel) => void;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  error?: boolean;
  href?: string;
  cyprus?: boolean;
  borderRadius?: string;
  popularSearchItems?: SearchViewModel[];
  onPopularSearchItemSelected?: (item: SearchViewModel) => void;
}

export const BookerSearchDrawer = ({
  placeholder,
  type,
  onInputLocationChange,
  fetchItems,
  onHrefUpdate,
  onSearchItemTextUpdate,
  history,
  onClearClick,
  callBack,
  onRemoveItem,
  error,
  href,
  cyprus,
  borderRadius,
  popularSearchItems,
  onPopularSearchItemSelected,
}: BookerSearchDrawerProps) => {
  const [showPastSearch, setShowPastSearch] = useState(false);
  const [location] = useState<BookerSearchViewModel>();
  const [inputLocation, setInputLocation] = useState<string>(
    location?.text || ''
  );
  const debouncedLocation = useDebounceValue<string>(inputLocation);

  const { data: locations = [], isError: searchTextError } = useQuery(
    ['locationItems', debouncedLocation],
    () => fetchItems(debouncedLocation.toLowerCase()),
    { enabled: debouncedLocation.length >= SEARCH_LENGTH }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setInputLocation(e.target.value);

  const handleLocationClicked = (item: BookerSearchViewModel): void => {
    onSearchItemTextUpdate?.(item.text || '');
    onHrefUpdate?.(item.url || '');
    setInputLocation(item.text || '');
    onInputLocationChange?.(item);

    if (type === 'Tour') {
      const currentHref = href?.split('?')?.[1];
      onHrefUpdate?.(`${item.url}${currentHref ? `?${currentHref}` : ''}`);
      return;
    }
    onHrefUpdate?.(item.url || '');
  };

  const validHistory = history && history?.length > 0;

  return (
    <>
      <HotelSeachWrapper borderRadius={borderRadius} cyprus={cyprus}>
        <SearchBarSearchInput
          label='Nereye Gideceksiniz?'
          inputProps={{
            value: inputLocation,
            placeholder,
            onChange: handleChange,
            onFocus: () => {
              setShowPastSearch(true);
            },
            inputMode: 'search',
          }}
          leftIcon={() => (
            <CustomIcon
              fontSize={20}
              paletteColor='coral'
              icon={LocationIcon}
            />
          )}
          popoverProps={{
            align: 'start',
          }}
          error={error}
          searchTextError={searchTextError}
          SearchResultListComponent={({ onChange }) => {
            return (validHistory && inputLocation.length === 0) ||
              (popularSearchItems && popularSearchItems?.length > 0) ||
              (inputLocation.length >= SEARCH_LENGTH &&
                locations?.length > 0) ? (
              <SearchResult
                onChange={(item: BookerSearchViewModel) => {
                  onChange(item.text);
                  handleLocationClicked(item);
                }}
                searchText={inputLocation || ''}
                inputLocation={inputLocation}
                data={locations || []}
                history={history}
                showPastSearch={showPastSearch}
                onClearClick={onClearClick}
                callBack={callBack}
                onRemoveItem={onRemoveItem}
                popularSearchItems={popularSearchItems}
                onPopularSearchItemSelected={onPopularSearchItemSelected}
              />
            ) : null;
          }}
        />
      </HotelSeachWrapper>
    </>
  );
};
